import React, { useState, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import '../../../../styles/desertFlora.css'
import './recipeBuilderV2.css'
import useRecipeState, { CAT_COLORS, COST_TYPE_META } from './useRecipeState'
import RecipeHeader from './RecipeHeader'
import RecipeTabs from './RecipeTabs'
import ComponentsTab from './ComponentsTab'
import InputsTab from './InputsTab'
import MaterialsTab from './MaterialsTab'
import ChildRecipesTab from './ChildRecipesTab'
import VariationsTab from './VariationsTab'
import LivePreviewRail from './LivePreviewRail'

/* ─── Confirm modal ─── */
const ConfirmModal = ({ modal, onClose }) => {
  if (!modal) return null
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{modal.title}</div>
        {modal.subtitle && <div className="modal-subtitle">{modal.subtitle}</div>}
        <div className="modal-message">{modal.message}</div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => { modal.onConfirm(); onClose() }}>
            {modal.actionLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Recipe list table ─── */
const RecipeList = ({ svcList, tMap, search, setSearch, catFilter, setCatFilter, categories, allWithRecipes, onEdit }) => {
  const [expanded, setExpanded] = useState({})
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font-ui)' }}>
      {/* Top bar */}
      <div className="recipe-list-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.5 }}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Service Recipes</span>
          <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ink-4)', padding: '2px 7px', border: '1px solid var(--line)', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Builder</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search services..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="bare"
            value={catFilter}
            onChange={e => setCatFilter(e.target.value)}
            style={{ minWidth: 180, padding: '8px 14px', border: '1px solid var(--line)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
          >
            {categories.map(c => <option key={c} value={c}>{c === 'All' ? `All Categories (${allWithRecipes.length})` : c}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
            {svcList.length} service{svcList.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      <div style={{ margin: '0 32px 40px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
        <table className="recipe-list-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Service</th>
              <th>Category</th>
              <th>Components</th>
              <th>Variations</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {svcList.map((s, i) => {
              const t = tMap[s.serviceId]
              const compArr = t?.components || []
              const varArr = t?.variations || []
              const childArr = t?.childRecipes || []
              const isExp = expanded[i]
              const catColor = CAT_COLORS[s.category] || '#6b7280'

              const inputSlots = t?.inputSlots || []
              const matArr = t?.defaultMaterials || []

              return (
                <React.Fragment key={s._id || i}>
                <tr onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))} style={{ cursor: 'pointer' }}>
                  <td style={{ textAlign: 'center', color: 'var(--ink-4)', fontSize: 12 }}>
                    {isExp ? '\u25BE' : '\u25B8'}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: 13 }}>{s.name}</div>
                    <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--ink-4)', marginTop: 2 }}>{s.serviceId}</div>
                  </td>
                  <td>
                    <span className="cat-chip" style={{ color: catColor, backgroundColor: `${catColor}14` }}>{s.category}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {compArr.slice(0, 4).map((c, ci) => {
                        const meta = COST_TYPE_META[c.costType] || COST_TYPE_META.labor
                        return <span key={ci} className={`chip ${meta.chipClass}`}>{meta.label}</span>
                      })}
                      {compArr.length > 4 && <span style={{ fontSize: 10, color: 'var(--ink-4)', alignSelf: 'center' }}>+{compArr.length - 4}</span>}
                      {childArr.length > 0 && (
                        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--subcontract)', background: 'var(--subcontract-soft)', padding: '2px 8px', borderRadius: 10 }}>
                          {childArr.length} child
                        </span>
                      )}
                      {compArr.length === 0 && childArr.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>{'\u2014'}</span>}
                    </div>
                  </td>
                  <td>
                    {varArr.length > 0 ? (
                      <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--primary)', background: 'var(--primary-soft)', padding: '2px 8px', borderRadius: 10 }}>
                        {varArr.length} var
                      </span>
                    ) : <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>{'\u2014'}</span>}
                  </td>
                  <td>
                    <button className="btn" onClick={e => { e.stopPropagation(); onEdit(i) }} style={{ fontSize: 12, padding: '6px 14px' }}>
                      Edit
                    </button>
                  </td>
                </tr>
                {isExp && t && (
                  <tr>
                    <td colSpan={6} style={{ padding: '12px 16px 16px 40px', background: 'var(--bg)', borderBottom: '2px solid var(--line)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                        {/* Components */}
                        <div>
                          <div className="eyebrow" style={{ marginBottom: 8 }}>Components ({compArr.length})</div>
                          {compArr.map((c, ci) => {
                            const meta = COST_TYPE_META[c.costType] || COST_TYPE_META.labor
                            return (
                              <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12 }}>
                                <span className={`chip ${meta.chipClass}`} style={{ fontSize: 8, padding: '1px 5px' }}>
                                  {meta.label}
                                </span>
                                <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{c.description || 'Untitled'}</span>
                                {c.laborRole && <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{c.laborRole}</span>}
                                {c.isOptional && <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', padding: '1px 4px', border: '1px solid var(--accent)', borderRadius: 3 }}>OPT</span>}
                                {c.followsLeadHours && <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--labor)', padding: '1px 4px', border: '1px solid var(--labor)', borderRadius: 3 }}>{'\u2193'} FOLLOWS</span>}
                              </div>
                            )
                          })}
                          {compArr.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>{'\u2014'}</div>}
                        </div>

                        {/* Inputs */}
                        <div>
                          <div className="eyebrow" style={{ marginBottom: 8 }}>Inputs ({inputSlots.length})</div>
                          {inputSlots.map((slot, si) => (
                            <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12 }}>
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', background: 'var(--bg-2)', padding: '2px 6px', borderRadius: 4 }}>{slot.unit || '?'}</span>
                              <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{slot.label || slot.key}</span>
                            </div>
                          ))}
                          {inputSlots.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>{'\u2014'}</div>}
                        </div>

                        {/* Variations */}
                        <div>
                          <div className="eyebrow" style={{ marginBottom: 8 }}>Variations ({varArr.length})</div>
                          {varArr.map((v, vi) => (
                            <div key={vi} style={{ padding: '5px 0', fontSize: 12 }}>
                              <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{v.label || v.attributeId}</div>
                              {v.type === 'select' && v.options && (
                                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                                  {v.options.map(o => o.label || o.value).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                          {varArr.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>{'\u2014'}</div>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>

              )
            })}
          </tbody>
        </table>
        {svcList.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
            No services match your search
          </div>
        )}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   RecipeBuilderV2 — Root component
   ═══════════════════════════════════════════════════════════ */
const RecipeBuilderV2 = ({ initialServiceId, modal: modalMode, onClose: onModalClose }) => {
  const state = useRecipeState()
  const [confirmModal, setConfirmModal] = useState(null)

  const {
    svcList, tMap, search, setSearch, catFilter, setCatFilter, categories, allWithRecipes,
    editIdx, setEditIdx, section, setSection,
    svc, tmpl, dirty, setDirty, markDirty, saving,
    comps, setComps, vars, setVars,
    inputSlots, setInputSlots,
    defaultMaterials, setDefaultMaterials,
    childRecipes, setChildRecipes,
    minimumTotal, setMinimumTotal,
    pvSel, setPvSel, slotValues, setSlotValues,
    classBreakdowns, setClassBreakdowns,
    preview, previewCalc,
    save, loading,
    laborRates, equipmentRates, productionRates, services
  } = state

  // Auto-open the editor when initialServiceId is set (modal usage)
  useEffect(() => {
    if (initialServiceId && svcList.length > 0 && editIdx === null) {
      const idx = svcList.findIndex(s => s.serviceId === initialServiceId)
      if (idx >= 0) { setEditIdx(idx); setSection('components') }
    }
  }, [initialServiceId, svcList, editIdx, setEditIdx, setSection])

  const openEditor = useCallback((i) => {
    if (dirty) {
      setConfirmModal({
        title: 'Unsaved Changes', subtitle: 'You have unsaved work',
        message: 'Switching services will discard your unsaved changes.',
        actionLabel: 'Discard & Switch',
        onConfirm: () => { setEditIdx(i); setSection('components'); setDirty(false) }
      })
      return
    }
    setEditIdx(i); setSection('components')
  }, [dirty, setEditIdx, setSection, setDirty])

  const closeEditor = useCallback(() => {
    if (dirty) {
      setConfirmModal({
        title: 'Unsaved Changes', subtitle: 'You have unsaved work',
        message: 'Closing will discard your unsaved changes.',
        actionLabel: 'Discard & Close',
        onConfirm: () => {
          setEditIdx(null); setDirty(false)
          if (modalMode && onModalClose) onModalClose()
        }
      })
      return
    }
    setEditIdx(null)
    if (modalMode && onModalClose) onModalClose()
  }, [dirty, setEditIdx, setDirty, modalMode, onModalClose])

  const onConfirm = useCallback((modal) => setConfirmModal(modal), [])

  const tabCounts = {
    components: comps.length,
    inputs: inputSlots.length,
    materials: defaultMaterials.length,
    childRecipes: childRecipes.length,
    variations: vars.length
  }

  if (loading) {
    return (
      <div className="desert-flora recipe-v2" style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ color: 'var(--ink-4)', fontSize: 14 }}>Loading recipes...</div>
      </div>
    )
  }

  // Editor mode
  if (editIdx !== null && svc) {
    const tabContent = {
      components: (
        <ComponentsTab
          comps={comps} setComps={v => { setComps(v); markDirty() }}
          laborRates={laborRates} equipmentRates={equipmentRates} productionRates={productionRates}
          onConfirm={onConfirm}
        />
      ),
      inputs: (
        <InputsTab
          slots={inputSlots} setSlots={v => { setInputSlots(v); markDirty() }}
          productionRates={productionRates}
          onConfirm={onConfirm}
        />
      ),
      materials: (
        <MaterialsTab
          materials={defaultMaterials} setMaterials={v => { setDefaultMaterials(v); markDirty() }}
          inputSlots={inputSlots}
          onConfirm={onConfirm}
        />
      ),
      childRecipes: (
        <ChildRecipesTab
          childRecipes={childRecipes} setChildRecipes={v => { setChildRecipes(v); markDirty() }}
          services={services}
          inputSlots={inputSlots}
          onConfirm={onConfirm}
        />
      ),
      variations: (
        <VariationsTab
          vars={vars} setVars={v => { setVars(v); markDirty() }}
          comps={comps}
          inputSlots={inputSlots}
          laborRates={laborRates}
          equipmentRates={equipmentRates}
          onConfirm={onConfirm}
        />
      )
    }

    return (
      <div className="desert-flora recipe-v2" style={{ position: 'fixed', inset: 0, zIndex: 100 }}>
        <div className="builder-layout">
          <div className="builder-main">
            <RecipeHeader
              svc={svc} tmpl={tmpl} dirty={dirty} saving={saving}
              minimumTotal={minimumTotal} setMinimumTotal={setMinimumTotal}
              onSave={save} onClose={closeEditor} markDirty={markDirty}
            />
            <RecipeTabs active={section} onChange={setSection} counts={tabCounts} />
            <div className="builder-content">
              {tabContent[section]}
            </div>
          </div>
          <div className="builder-rail">
            <LivePreviewRail
              preview={preview} previewCalc={previewCalc}
              vars={vars} pvSel={pvSel} setPvSel={v => { setPvSel(v); markDirty() }}
              inputSlots={inputSlots} slotValues={slotValues} setSlotValues={setSlotValues}
              classBreakdowns={classBreakdowns} setClassBreakdowns={setClassBreakdowns}
              minimumTotal={minimumTotal}
            />
          </div>
        </div>
        <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
      </div>
    )
  }

  // Modal mode: don't show the list, just show loading until editor opens
  if (modalMode) {
    return (
      <div className="desert-flora recipe-v2" style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ color: 'var(--ink-4)', fontSize: 14 }}>Loading recipe...</div>
      </div>
    )
  }

  // List mode
  return (
    <div className="desert-flora recipe-v2">
      <RecipeList
        svcList={svcList} tMap={tMap}
        search={search} setSearch={setSearch}
        catFilter={catFilter} setCatFilter={setCatFilter}
        categories={categories} allWithRecipes={allWithRecipes}
        onEdit={openEditor}
      />
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(RecipeBuilderV2)
